import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_ORDERS_UPP } from 'features/consults/queries';
import { GET_ORDER_DETAILS } from 'features/results/queries';
import { useFeatureFlags } from 'helpers/hooks';
import type { GetMyRiskOrders_orders_OrdersListSuccess_ordersList } from 'gql/typeMigrations';
import { GET_ORDERS } from './queries';

export const getPatientName = (
  firstName: string | null,
  lastName: string | null,
) => `${firstName} ${lastName}`;

export function useOrderDetails(accessionId: string) {
  const { data } = useQuery(GET_ORDERS, {
    skip: !accessionId,
    fetchPolicy: 'cache-first',
  });

  const matchingOrder = useMemo(() => {
    if (
      data?.orders?.__typename === 'OrdersListSuccess' &&
      data?.orders?.ordersList?.length
    ) {
      return data.orders.ordersList.find(
        (order) => order?.accessionId === accessionId,
      );
    }

    return null;
  }, [accessionId, data?.orders]);

  return matchingOrder;
}

// TODO with Results updates: use external ID to support both oncology and prenatal
export function useOrderDetailsUPP(accessionId: string) {
  const { uppPrenatalResultsEnabled } = useFeatureFlags();
  const { data } = useQuery(GET_ORDERS_UPP, {
    skip: !accessionId,
    fetchPolicy: 'cache-first',
    variables: {
      myriskOnly: !uppPrenatalResultsEnabled,
    },
  });

  const matchingOrder = useMemo(() => {
    if (
      data?.orders?.__typename === 'OrdersListSuccess' &&
      data?.orders?.ordersList?.length
    ) {
      return data.orders.ordersList.find(
        (order) => order?.accessionId === accessionId,
      );
    }

    return null;
  }, [accessionId, data?.orders]);

  return matchingOrder;
}

// TODO clean the others up (they're technically using orders list instead of order details)
export function useOrderDetailsV2(externalId: string) {
  const query = useQuery(GET_ORDER_DETAILS, {
    skip: !externalId,
    fetchPolicy: 'cache-first',
    variables: {
      externalId,
    },
  });

  return query;
}

export function isMyRiskResult(
  result: GetMyRiskOrders_orders_OrdersListSuccess_ordersList,
) {
  return result.product!.toLowerCase().includes('myrisk');
}

export function isBRACAnalysisCDxResult(
  result: GetMyRiskOrders_orders_OrdersListSuccess_ordersList,
) {
  return result.product!.toLowerCase().includes('bracanalysis cdx');
}
